import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import {
  EntitySelectComponent,
  EntitySelectPage
} from '../../../../../shared/components/entity-select/entity-select.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { UserApiService } from '../../../../users/services/user-api.service';
import { CrmUserResponse } from '../../../../users/models/user.model';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketSource } from '../../../models/tickets.model';
import {
  TicketAssignmentRule,
  TicketAssignmentStrategy,
  TicketCategory,
  TicketPipeline,
  TicketPriority,
  TicketTeam
} from '../../../models/tickets-config.model';

/** Shape del JSON `conditions` que evalúa el backend (TicketAssignmentResolverListener). */
interface AssignmentConditions {
  priorities?: number[];
  categories?: number[];
  pipelines?: number[];
  sources?: string[];
}

/** Shape del JSON `strategyConfig`: teamId (round-robin/workload/team-queue) o userId (fixed). */
interface AssignmentStrategyConfig {
  teamId?: number;
  userId?: number;
}

@Component({
  selector: 'app-tickets-assignment-rules-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent, EntitySelectComponent],
  templateUrl: './assignment-rules-form.component.html'
})
export class TicketsAssignmentRulesFormComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<TicketAssignmentRule>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);
  private userApi = inject(UserApiService);
  private auth = inject(AuthService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';

  readonly strategies: TicketAssignmentStrategy[] = [
    'ROUND_ROBIN',
    'SKILL_BASED',
    'WORKLOAD',
    'FIXED_USER',
    'TEAM_QUEUE'
  ];

  /** Etiquetas legibles de las estrategias. */
  readonly strategyLabels: Record<TicketAssignmentStrategy, string> = {
    ROUND_ROBIN: 'Round-robin (equipo)',
    SKILL_BASED: 'Por habilidades (equipo)',
    WORKLOAD: 'Menor carga (equipo)',
    FIXED_USER: 'Usuario fijo',
    TEAM_QUEUE: 'Cola de equipo'
  };

  /** Orígenes de ticket disponibles para la condición `sources`. */
  readonly sources: { value: TicketSource; label: string }[] = [
    { value: 'MANUAL', label: 'Manual' },
    { value: 'EMAIL', label: 'Correo' },
    { value: 'CHAT', label: 'Chat' },
    { value: 'PHONE', label: 'Teléfono' },
    { value: 'WEB_FORM', label: 'Formulario web' },
    { value: 'PORTAL', label: 'Portal' },
    { value: 'SOCIAL', label: 'Redes sociales' },
    { value: 'API', label: 'API' }
  ];

  // Catálogos para los selectores visuales de condiciones.
  priorities: TicketPriority[] = [];
  categories: TicketCategory[] = [];
  pipelines: TicketPipeline[] = [];
  teams: TicketTeam[] = [];

  // Condiciones seleccionadas (se construyen en el objeto `conditions` al guardar).
  condPriorities: number[] = [];
  condCategories: number[] = [];
  condPipelines: number[] = [];
  condSources: TicketSource[] = [];

  // Config de estrategia (una u otra según la estrategia elegida).
  strategyTeamId: number | null = null;
  strategyUserId: number | null = null;
  /** Texto a mostrar en el EntitySelect de usuario al editar. */
  strategyUserDisplay = '';

  /** Cache de usuarios activos (el endpoint no busca por texto → filtramos en cliente). */
  private usersCache: CrmUserResponse[] | null = null;

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: [''],
    priority: [10],
    strategy: ['ROUND_ROBIN' as TicketAssignmentStrategy, [Validators.required]],
    isActive: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.cargarCatalogos();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.auth.getCurrentUser()?.businessId || 1;
  }

  private cargarCatalogos(): void {
    forkJoin({
      priorities: this.configApi.getPriorities().pipe(catchError(() => of([] as TicketPriority[]))),
      categories: this.configApi.getCategories().pipe(catchError(() => of([] as TicketCategory[]))),
      pipelines: this.configApi.getPipelines().pipe(catchError(() => of([] as TicketPipeline[]))),
      teams: this.configApi.getTeams().pipe(catchError(() => of([] as TicketTeam[])))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ priorities, categories, pipelines, teams }) => {
        this.priorities = priorities;
        this.categories = categories;
        this.pipelines = pipelines;
        this.teams = teams;
        // INC 65: el orden de evaluación se muestra como un nombre de prioridad;
        // reconcilia el valor numérico existente con el `level` más cercano.
        this.normalizePriorityControl();
      });
  }

  /** Etiqueta legible de una prioridad para el dropdown de orden (INC 65). */
  priorityOptionLabel(p: TicketPriority): string {
    return (p.label && p.label.trim()) || p.name;
  }

  /**
   * INC 65: el control `priority` guarda el orden de evaluación como el `level`
   * de una prioridad. Si el valor actual no coincide con ningún `level`
   * disponible (regla antigua), lo ajusta al `level` más cercano para que el
   * dropdown pueda preseleccionar una opción.
   */
  private normalizePriorityControl(): void {
    if (!this.priorities.length) return;
    const ctrl = this.form.get('priority');
    const current = Number(ctrl?.value ?? 10);
    const levels = this.priorities.map((p) => p.level);
    if (levels.includes(current)) return;
    const closest = levels.reduce((a, b) =>
      Math.abs(b - current) < Math.abs(a - current) ? b : a
    );
    ctrl?.setValue(closest, { emitEvent: false });
  }

  // ===== EntitySelect: usuarios (arrow fns para conservar `this`) =====
  userSearchFn = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const build = (list: CrmUserResponse[]): EntitySelectPage<CrmUserResponse> => {
      const t = term.trim().toLowerCase();
      const items = t
        ? list.filter(
            (u) =>
              `${u.fullname} ${u.lastname}`.toLowerCase().includes(t) ||
              (u.mail || '').toLowerCase().includes(t)
          )
        : list;
      return { items, totalPages: 1 };
    };
    if (this.usersCache) {
      return of(build(this.usersCache));
    }
    return this.userApi.listActiveByBusiness(this.businessId).pipe(
      map((list) => {
        this.usersCache = list;
        return build(list);
      })
    );
  };

  userLabelFn = (u: CrmUserResponse): string => `${u.fullname} ${u.lastname}`.trim();
  userSublabelFn = (u: CrmUserResponse): string => u.mail || '';
  userIdFn = (u: CrmUserResponse): number => u.id;

  // ===== Estrategia: ¿usa usuario fijo o equipo? =====
  get strategy(): TicketAssignmentStrategy {
    return this.form.get('strategy')!.value as TicketAssignmentStrategy;
  }

  get needsUser(): boolean {
    return this.strategy === 'FIXED_USER';
  }

  // ===== Toggles de condiciones (checkbox groups) =====
  toggleNumber(list: number[], id: number): void {
    const i = list.indexOf(id);
    if (i >= 0) list.splice(i, 1);
    else list.push(id);
  }

  toggleSource(value: TicketSource): void {
    const i = this.condSources.indexOf(value);
    if (i >= 0) this.condSources.splice(i, 1);
    else this.condSources.push(value);
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.condPriorities = [];
    this.condCategories = [];
    this.condPipelines = [];
    this.condSources = [];
    this.strategyTeamId = null;
    this.strategyUserId = null;
    this.strategyUserDisplay = '';
    this.form.reset({
      name: '',
      description: '',
      priority: 10,
      strategy: 'ROUND_ROBIN',
      isActive: true
    });
  }

  loadRule(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getAssignmentRuleById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          priority: item.priority ?? 10,
          strategy: item.strategy,
          isActive: item.isActive
        });
        this.originalName = item.name;

        const cond = (item.conditions ?? {}) as AssignmentConditions;
        this.condPriorities = Array.isArray(cond.priorities) ? [...cond.priorities] : [];
        this.condCategories = Array.isArray(cond.categories) ? [...cond.categories] : [];
        this.condPipelines = Array.isArray(cond.pipelines) ? [...cond.pipelines] : [];
        this.condSources = Array.isArray(cond.sources) ? [...cond.sources] as TicketSource[] : [];

        const sc = (item.strategyConfig ?? {}) as AssignmentStrategyConfig;
        this.strategyTeamId = sc.teamId ?? null;
        this.strategyUserId = sc.userId ?? null;
        this.resolveUserDisplay(this.strategyUserId);
        // INC 65: preseleccionar la opción del dropdown de prioridad.
        this.normalizePriorityControl();
      },
      error: (err) => { this.error = err.error?.message || 'Error al cargar regla'; }
    });
  }

  /** Al editar, obtiene el nombre del usuario fijo para mostrarlo en el EntitySelect. */
  private resolveUserDisplay(userId: number | null): void {
    if (userId == null) { this.strategyUserDisplay = ''; return; }
    this.userApi.listActiveByBusiness(this.businessId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (list) => {
        this.usersCache = list;
        const u = list.find((x) => x.id === userId);
        this.strategyUserDisplay = u ? this.userLabelFn(u) : `Usuario #${userId}`;
      },
      error: () => { this.strategyUserDisplay = `Usuario #${userId}`; }
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }

    // Validación de la configuración de estrategia según el tipo.
    if (this.needsUser && this.strategyUserId == null) {
      this.error = 'Selecciona el usuario al que se asignará (estrategia Usuario fijo).';
      return;
    }
    if (!this.needsUser && this.strategyTeamId == null) {
      this.error = 'Selecciona el equipo para la estrategia elegida.';
      return;
    }

    this.saving = true;
    this.error = null;
    const raw = this.form.value;

    // Construir el objeto `conditions` exactamente como lo espera el backend.
    const conditions: AssignmentConditions = {};
    if (this.condPriorities.length) conditions.priorities = [...this.condPriorities];
    if (this.condCategories.length) conditions.categories = [...this.condCategories];
    if (this.condPipelines.length) conditions.pipelines = [...this.condPipelines];
    if (this.condSources.length) conditions.sources = [...this.condSources];
    const conditionsPayload = Object.keys(conditions).length ? conditions : undefined;

    // Construir el objeto `strategyConfig` (teamId o userId).
    const strategyConfig: AssignmentStrategyConfig = this.needsUser
      ? { userId: this.strategyUserId! }
      : { teamId: this.strategyTeamId! };

    const req = {
      name: raw.name,
      description: raw.description || undefined,
      priority: Number(raw.priority ?? 10),
      conditions: conditionsPayload,
      strategy: raw.strategy as TicketAssignmentStrategy,
      strategyConfig,
      isActive: !!raw.isActive
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateAssignmentRule(this.itemId, req)
      : this.configApi.createAssignmentRule(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Regla de asignación',
          this.originalName !== (raw.name?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.name?.trim() || ''}".`
            : `Se actualizó "${raw.name?.trim() || ''}" correctamente.`);
        else this.toast.created('Regla de asignación', `Se creó "${raw.name?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar la regla';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
